import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProdutividade } from '../../../services/conferente';
import type { ProdutividadeResponse } from '../../../types/conferencia';
import '../Conferencia.css';

const ConferenciaProdutividade: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<ProdutividadeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mes');
  const [tipoFiltro, setTipoFiltro] = useState('');

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getProdutividade({ periodo, tipo: tipoFiltro || undefined });
      setData(result);
    } catch (error) {
      console.error('Erro ao carregar produtividade:', error);
    } finally {
      setLoading(false);
    }
  }, [periodo, tipoFiltro]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  if (loading) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page-container">
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
          <p>Erro ao carregar dados</p>
        </div>
      </div>
    );
  }

  const { totais, ranking } = data;

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="conferencia-back-link" onClick={() => navigate('/conferencia')} style={{ marginBottom: 0 }}>
            <i className="fas fa-arrow-left"></i>
          </button>
          <div>
            <h1><i className="fas fa-chart-bar" style={{ marginRight: 8, color: '#27ae60' }}></i> Produtividade dos Conferentes</h1>
            <p style={{ margin: 0, fontSize: '0.85rem', color: '#7f8c8d' }}>Acompanhe o desempenho da equipe de conferência</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <select className="form-control" value={periodo} onChange={(e) => setPeriodo(e.target.value)} style={{ width: 'auto' }}>
            <option value="dia">Hoje</option>
            <option value="semana">Esta semana</option>
            <option value="mes">Este mês</option>
            <option value="ano">Este ano</option>
          </select>
          <select className="form-control" value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Todos os tipos</option>
            <option value="pre_carregamento">Pré-Carregamento</option>
            <option value="carregamento">Carregamento</option>
            <option value="descarregamento">Descarregamento</option>
          </select>
        </div>
      </div>

      {/* Cards de Totais */}
      <div className="conferencia-stats-grid">
        <div className="conferencia-stat-card">
          <div className="stat-header">
            <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
            <span>Total Conferências</span>
          </div>
          <p className="stat-value">{totais.total_conferencias}</p>
          <p className="stat-sub" style={{ color: '#27ae60' }}>{totais.concluidas} concluídas</p>
        </div>
        <div className="conferencia-stat-card">
          <div className="stat-header">
            <i className="fas fa-users" style={{ color: '#3498db' }}></i>
            <span>Conferentes Ativos</span>
          </div>
          <p className="stat-value">{totais.total_conferentes}</p>
        </div>
        <div className="conferencia-stat-card">
          <div className="stat-header">
            <i className="fas fa-chart-line" style={{ color: '#8e44ad' }}></i>
            <span>Itens Conferidos</span>
          </div>
          <p className="stat-value">{totais.total_itens}</p>
        </div>
        <div className="conferencia-stat-card">
          <div className="stat-header">
            <i className="fas fa-exclamation-triangle" style={{ color: '#e74c3c' }}></i>
            <span>Divergências</span>
          </div>
          <p className="stat-value">{totais.total_divergencias}</p>
          <p className="stat-sub" style={{ color: '#e74c3c' }}>{totais.divergentes} conferências</p>
        </div>
        <div className="conferencia-stat-card">
          <div className="stat-header">
            <i className="fas fa-box" style={{ color: '#e67e22' }}></i>
            <span>Por Tipo</span>
          </div>
          <div style={{ fontSize: '0.75rem', marginTop: 4 }}>
            <p style={{ margin: '2px 0', color: '#e65100' }}>Pré-Carreg: {totais.pre_carregamentos}</p>
            <p style={{ margin: '2px 0', color: '#1565c0' }}>Carreg: {totais.carregamentos}</p>
            <p style={{ margin: '2px 0', color: '#2e7d32' }}>Descarreg: {totais.descarregamentos}</p>
          </div>
        </div>
      </div>

      {/* Ranking */}
      <div className="card">
        <div className="card-header">
          <h2><i className="fas fa-users" style={{ marginRight: 8, color: '#27ae60' }}></i> Ranking de Conferentes</h2>
          <span style={{ fontSize: '0.85rem', color: '#7f8c8d' }}>{ranking.length} conferentes</span>
        </div>
        <div className="data-table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Conferente</th>
                <th>Filial</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Concluídas</th>
                <th style={{ textAlign: 'center' }}>Pré-Carreg</th>
                <th style={{ textAlign: 'center' }}>Carreg</th>
                <th style={{ textAlign: 'center' }}>Descarreg</th>
                <th style={{ textAlign: 'center' }}>Itens</th>
                <th style={{ textAlign: 'center' }}>Diverg.</th>
                <th style={{ textAlign: 'center' }}>Tempo Médio</th>
                <th style={{ textAlign: 'center' }}>Detalhes</th>
              </tr>
            </thead>
            <tbody>
              {ranking.length === 0 ? (
                <tr><td colSpan={12} style={{ textAlign: 'center', padding: '40px 20px', color: '#7f8c8d' }}>Nenhum dado de produtividade no período selecionado</td></tr>
              ) : (
                ranking.map((item, index) => (
                  <tr key={item.usuario_id}>
                    <td>
                      <span className={`ranking-badge ${index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? 'bronze' : 'default'}`}>
                        {index + 1}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontWeight: 500 }}>{item.conferente_nome}</span>
                      <p style={{ fontSize: '0.75rem', color: '#7f8c8d', margin: 0 }}>{item.conferente_email}</p>
                    </td>
                    <td>
                      {item.filial_nome ? (
                        <span style={{ fontSize: '0.85rem', color: '#2c3e50' }}>
                          <i className="fas fa-building" style={{ marginRight: 4, color: '#7f8c8d', fontSize: '0.75rem' }}></i>
                          {item.filial_nome}
                        </span>
                      ) : (
                        <span style={{ color: '#bdc3c7', fontSize: '0.85rem' }}>-</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{item.total_conferencias}</td>
                    <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: 500 }}>{item.conferencias_concluidas}</td>
                    <td style={{ textAlign: 'center', color: '#e65100' }}>{item.pre_carregamentos}</td>
                    <td style={{ textAlign: 'center', color: '#1565c0' }}>{item.carregamentos}</td>
                    <td style={{ textAlign: 'center', color: '#2e7d32' }}>{item.descarregamentos}</td>
                    <td style={{ textAlign: 'center' }}>{item.total_itens_conferidos}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ color: item.total_divergencias > 0 ? '#e74c3c' : '#7f8c8d', fontWeight: item.total_divergencias > 0 ? 500 : 400 }}>
                        {item.total_divergencias}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, color: '#7f8c8d', fontSize: '0.85rem' }}>
                        <i className="fas fa-clock" style={{ fontSize: '0.75rem' }}></i>
                        {item.tempo_medio_minutos > 0 ? `${item.tempo_medio_minutos} min` : '-'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button className="btn-view" onClick={() => navigate(`/conferencia/produtividade/${item.usuario_id}${item.is_novo_conferente ? '?tipo=novo' : ''}`)} title="Ver detalhes">
                        <i className="fas fa-chevron-right"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Leituras por dia */}
      {data.leituras_por_dia.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <div className="card-header">
            <h2><i className="fas fa-calendar-alt" style={{ marginRight: 8, color: '#3498db' }}></i> Leituras por Dia</h2>
          </div>
          <div className="data-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th style={{ textAlign: 'center' }}>Total</th>
                  <th style={{ textAlign: 'center' }}>OK</th>
                  <th style={{ textAlign: 'center' }}>Duplicados</th>
                  <th style={{ textAlign: 'center' }}>Não Encontrados</th>
                </tr>
              </thead>
              <tbody>
                {data.leituras_por_dia.map(dia => (
                  <tr key={dia.data}>
                    <td style={{ fontWeight: 500 }}>{new Date(dia.data).toLocaleDateString('pt-BR')}</td>
                    <td style={{ textAlign: 'center', fontWeight: 600 }}>{dia.total_leituras}</td>
                    <td style={{ textAlign: 'center', color: '#27ae60' }}>{dia.leituras_ok}</td>
                    <td style={{ textAlign: 'center', color: '#f39c12' }}>{dia.duplicados}</td>
                    <td style={{ textAlign: 'center', color: '#e74c3c' }}>{dia.nao_encontrados}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConferenciaProdutividade;
